<template>
    <div class="container-details">
        <AlertDismiss ref="alertDismiss"></AlertDismiss>
        <BaseModal ref="modalEvaluation" v-model="modalEvaluation" size="lg">
            <EvaluationUserForm></EvaluationUserForm>
        </BaseModal>

        <BaseModal ref="modalAddTheme" v-model="modalAddTheme" size="lg">
            <CurriculumForm :courseId="courseId"/>
        </BaseModal>

        <CurriculumDeleteModal/>

        <div v-if="isLoading" class="d-flex justify-content-center">
            <b-spinner></b-spinner>
        </div>

        <template v-else>
            <div v-if="emptyCourse" class="card mb-2">
                <div class="card-body text-center">
                    <h5 class="mb-0">
                        Aún no se ha creado ningún tema
                    </h5>
                </div>
            </div>

            <div v-else class="row">
                <div class="col-3">
                    <h6>Temas</h6>
                    <div class="list-group" v-for="theme in themes" :key="theme.id">
                        <div :class="getClassTheme(theme)" @click.prevent="onClickTheme(theme)">
                            {{ theme.name }}
                            <div class="flex-grow-1"></div>
                            <div
                                v-permission="'capacitaciones-tema-eliminar'"
                                v-if="isSelected(theme)"
                                v-b-tooltip.hover
                                title="Eliminar tema"
                            >
                                <button class="btn btn-light btn-sm opacity-75" @click.stop="deleteTheme(theme)">
                                    <b-icon icon="trash-fill" variant="secondary"></b-icon>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="selected" class="col-9">
                    <b-overlay :show="showThemeContentOverlay" :opacity="0.8" spinner-type="grow">
                        <b-card no-body>
                            <b-tabs small card>
                                <b-tab title="Recursos" active>
                                    <ResourceTable
                                        :items="selected.materials"
                                        :curriculumId="selected.id"
                                        :courseId="courseId"
                                    />
                                </b-tab>
                                <b-tab title="Evaluaciones">
                                    <EvaluationTable
                                        :items="selected.evaluations"
                                        :curriculumId="selected.id"
                                        :courseId="courseId"
                                    />
                                </b-tab>
                            </b-tabs>
                        </b-card>
                    </b-overlay>
                </div>
            </div>
        </template>

        <button
            v-permission="'capacitaciones-tema-crear'"
            class="btn-add btn btn-outline-primary"
            @click="openModalAddTheme"
        >
            <b-icon icon="plus"/>
            Añadir tema
        </button>
    </div>
</template>


<script>
import AlertDismiss from '@/components/shared/Alert-dismiss.vue';
import EvaluationUserForm from './CourseDetails/EvaluationTable/Evaluation-user-form.vue';
import BaseModal from '@/components/shared/Base-modal.vue';
import { mapActions } from 'vuex';
import { EventNames } from '@/plugins/enums/index.js'
import ResourceTable from './CourseDetails/Resource-table.vue';
import EvaluationTable from './CourseDetails/Evaluation-table.vue';
import CurriculumForm from './CourseDetails/Curriculum-form.vue';
import CurriculumDeleteModal from './CourseDetails/Curriculum-delete-modal.vue';

export default {
    name: 'Course-details',
    components: {
        ResourceTable,
        EvaluationTable,
        AlertDismiss,
        EvaluationUserForm,
        BaseModal,
        CurriculumForm,
        CurriculumDeleteModal,
    },

    props: {
        courseId: String
    },

    data() {
        return {
            modalEvaluation: false,
            modalAddTheme: false,
            course: null,
            selected: null,
            showThemeContentOverlay: false,
            isLoading: true,
        }
    },

    computed: {
        emptyCourse() {
            return this.themes.length === 0;
        },

        themes() {
            return this.course?.themes || [];
        }
    },

    async mounted() {
        await this.initializeComponent();

        this.$eventBus.$on(EventNames.UpdateCurrentCurriculum, async ({ courseId }) => {
            await this.updateCourse(courseId);
        });

        this.$eventBus.$on(EventNames.UpdateCurriculumList, async (courseId) => {
            await this.updateCourse(courseId);
        });

        this.$eventBus.$on(EventNames.SubmittedEvaluation, async () => {
            await this.updateCourse(this.courseId)
        })
    },

    beforeDestroy() {
        // Clean up event listeners
        this.$eventBus.$off(EventNames.UpdateCurrentCurriculum);
        this.$eventBus.$off(EventNames.UpdateCurriculumList);
    },

    methods: {
        ...mapActions(["getCourseById"]),

        async initializeComponent() {
            try {
                this.isLoading = true;
                await this.getCourse();
                if (this.themes.length > 0) {
                    this.selected = this.themes[0];
                }
            } finally {
                this.isLoading = false;
            }
        },

        async getCourse() {
            try {
                const result = await this.getCourseById({ courseId: this.courseId });
                if (result.isError) {
                    this.$refs.alertDismiss.showAlert(result.message);
                    return;
                }
                this.course = { ...result.data };
            } catch (error) {
                this.$refs.alertDismiss.showAlert('Error loading course');
            }
        },

        getClassTheme(theme) {
            const classes = ['list-group-item', 'list-group-item-action', 'd-flex', 'align-items-center', 'my-1'];
            if (theme.id === this.selected?.id) {
                classes.push('selected-theme');
            }
            return classes.join(' ');
        },

        onClickTheme(theme) {
            this.selected = theme;
            this.showThemeContentOverlay = true;
            setTimeout(() => {
                this.showThemeContentOverlay = false;
            }, 150);
        },

        async updateCourse(targetId) {
            if (targetId === this.courseId) {
                const previousSelectedId = this.selected?.id;
                await this.getCourse();

                if (previousSelectedId) {
                    this.selected = this.themes.find(theme => theme.id === previousSelectedId) ||
                                  (this.themes.length > 0 ? this.themes[0] : null);
                } else if (this.themes.length > 0) {
                    this.selected = this.themes[0];
                } else {
                    this.selected = null;
                }
            }
        },

        openModalAddTheme() {
            this.modalAddTheme = true;
        },

        deleteTheme(theme) {
            this.$eventBus.$emit(EventNames.OpenModalDeleteCurriculum, {curriculum: theme, courseId: this.courseId});
        },

        isSelected(theme) {
            return theme.id === this.selected?.id;
        }
    },
}
</script>

<style scoped>
.course-header {
    padding-bottom: 0.65rem;
    border-radius: 12px 12px 0 0;
    border-bottom: 2px solid #c7b1f8;
}

.course-header h4 {
    color: #006064;
    margin: 0;
    font-size: 1.5em;
    font-weight: 300;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.1);
}

.selected-theme {
    background-color: #f8f9fa;
    border-right: 3px solid var(--bs-primary);
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
    position: relative;
    overflow: hidden;
}

.list-group-item:hover {
    cursor: pointer;
}

.overlay {
    min-height: 380px;
}

.selected-theme::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(210, 187, 241, 0.2), transparent);
    animation: slide 0.8s ease;
}

@keyframes slide {
    0% {
        left: -100%;
    }

    100% {
        left: 100%;
    }
}

.btn-add {
    position: absolute;
    bottom: 43px;
    left: 1em;
}
</style>